import { format } from "date-fns";
import {
  type UTMParameters,
  type ClickIdParameters,
} from '../../components/Authentication/utils';
import { getRequest, postRequest } from "../requests";
import { resetTurnstile, toISOString } from '../utils';

export async function verifyPhoneNumber(phone: string, verificationCode: string): Promise<void> {
  return postRequest(`api/patientopenregistrationV2/verifyphonenumber`, { phone, verificationCode })
}

export async function login(emailOrPhone: string, password: string) {
  const response = await postRequest(`identityserver/local/patientopenregistrationV2/login`, { EmailOrPhone: emailOrPhone, Password: password }, true)
  resetTurnstile();
  return response;
}

export async function sendResetPasswordCode(phone: string) {
  const response = await postRequest(`api/patientopenregistrationV2/sendresetpasswordcode?phone=${encodeURIComponent(phone)}`, {}, true)
  resetTurnstile();
  return response;
}

export async function updateEmailAddress(clientStateID: string) {
  return getRequest(`api/researchkit/account/updateEmailAddress?clientStateID=${clientStateID}`)
}

export async function resetPasswordWithPhone(verifiedPhoneToken: string, password: string) {
  return postRequest(`identityserver/local/patientopenregistrationV2/resetpassword`, { verifiedPhoneToken, password })
}

export async function resetPasswordWithNonce(verifiedNonceToken: string, password: string) {
  return postRequest(`identityserver/local/patientopenregistrationV2/resetpassword`, { verifiedNonceToken, password })
}

export async function getAdministeredStudyEnrollment(projectCode: string) {
  return getRequest(`api/researchkit/administeredstudyenrollment/studies/${projectCode}?offset=${getCurrentOffset()}`)
}

export async function getRegistrationLink(linkKey: string) {
  return getRequest(`api/mydatahelps/registrationlink/${linkKey}`)
}


export async function validateAdministeredStudyEnrollmentIdentifier(projectCode: string, identifier: string) {
  return postRequest(`api/researchkit/administeredstudyenrollment/studies/${projectCode}/validateidentifier`, { identifier })
}

export async function getAccountStatus(emailOrPhone: string) {
  try {
    const response = await getRequest('api/patientopenregistrationV2/accountstatus', new URLSearchParams({ emailOrPhone }), true)
    return response
  } catch (error: any) {
    if (error?.message) {
      throw Error(error.message, { cause: error });
    } else {
      throw Error("participant-login-error-invalid-email-or-phone", { cause: error })
    }
  } finally {
    resetTurnstile();
  }
}

export async function sendVerificationCode(phone: string) {
  try {
    const response = await postRequest(`api/patientopenregistrationV2/sendverificationcode?phone=${encodeURIComponent(phone)}`, {}, true)
    return response
  } catch (error) {
    throw Error("participant-login-error-invalid-email-or-phone", { cause: error })
  } finally {
    resetTurnstile();
  }
}

export async function verifyPhone(phone: string, verificationCode: string) {
  try {
    const response = await postRequest(`api/patientopenregistrationV2/verifyphonenumber`, { phone, verificationCode })
    if (!response.VerifiedPhoneToken) {
      throw Error("participant-login-error-invalid-verification-code")
    }
    return response.VerifiedPhoneToken
  } catch (error) {
    throw Error("participant-login-error-invalid-verification-code", { cause: error })
  }
}

export async function finishLogin(emailOrPhone: string, password: string) {
  try {
    const response = await postRequest(`identityserver/local/patientopenregistrationV2/login`, { EmailOrPhone: emailOrPhone, Password: password }, true)
  } catch (error: any) {
    resetTurnstile();
    if (error?.message) {
      throw Error(error.message, { cause: error });
    } else {
      throw Error("participant-login-error-invalid-password", { cause: error })
    }
  }
}

export async function finishEmailJoin(email: string, password: string, utmParameters: UTMParameters, clickIdParameters: ClickIdParameters) {
  try {
    const response = await postRequest(`identityserver/local/patientopenregistrationV2/register`, {
      email: email,
      password: password,
      utmParameters,
      clickIdParameters,
    }, true)
    return response
  } catch (error) {
    resetTurnstile();
    throw Error("participant-login-error-registration-failed", { cause: error })
  }
}

export async function finishPhoneJoin(verifiedPhoneToken: string, password: string, utmParameters: UTMParameters, clickIdParameters: ClickIdParameters) {
  try {
    const response = await postRequest(`identityserver/local/patientopenregistrationV2/register`, {
      verifiedPhoneToken,
      password,
      utmParameters,
      clickIdParameters,
    }, true)
    return response
  } catch (error) {
    resetTurnstile();
    throw Error("participant-login-error-registration-failed", { cause: error })
  }
}

export async function forgotPassword(emailOrPhone: string) {
  try {
    const response = await postRequest(`api/patientopenregistrationV2/forgotpassword?emailOrPhone=${encodeURIComponent(emailOrPhone)}`, {}, true)
    return response
  } catch (error) {
    throw Error("participant-login-error-forgot-password-failed", { cause: error })
  } finally {
    resetTurnstile();
  }
}

export async function sendVerificationCodeForPasswordReset(phone: string) {
  try {
    const response = await postRequest(`api/patientopenregistrationV2/sendresetpasswordcode?phone=${encodeURIComponent(phone)}`, {}, true)
    return response
  } catch (error) {
    throw Error("participant-login-error-invalid-email-or-phone", { cause: error })
  } finally {
    resetTurnstile();
  }
}

export async function verifyPasswordResetNonce(email: string | null, phone: string | null, nonce: string) {
  try {
    if (!email && !phone) {
      throw Error("participant-login-error-invalid-email-or-phone")
    }
    const response = await postRequest(`api/patientopenregistrationV2/verifypasswordresetnonce`, { email: email, phone: phone, nonce: nonce })
    if (!response.VerifiedNonceToken) {
      throw Error("participant-login-error-invalid-password-reset-nonce")
    }
    return response.VerifiedNonceToken
  } catch (error) {
    throw Error("participant-login-error-invalid-password-reset-nonce", { cause: error })
  }
}

export async function verifyEmail(email: string, nonce: string) {
  try {
    const response = await postRequest(`api/patientopenregistrationV2/verifyemail`, { email, nonce })
    return response
  } catch (error) {
    throw Error("participant-login-error-invalid-verify-email-nonce", { cause: error })
  }
}

export async function verifyEmailUpdate(clientStateID: string) {
  try {
    const response = await getRequest(`api/researchkit/account/updateEmailAddress?clientStateID=${clientStateID}`)
    return response;
  } catch (error) {
    throw Error("update-contact-info-verify-email-server-error", { cause: error })
  }
}

export async function finishPasswordResetPhone(verifiedPhoneToken: string, password: string) {
  try {
    const response = await postRequest(`identityserver/local/patientopenregistrationV2/resetpassword`, { verifiedPhoneToken: verifiedPhoneToken, password: password })
    return response
  } catch (error) {
    throw Error("participant-login-error-password-reset-failed", { cause: error })
  }
}

export async function finishPasswordResetNonce(verifiedNonceToken: string, password: string) {
  try {
    const response = await postRequest(`identityserver/local/patientopenregistrationV2/resetpassword`, { verifiedNonceToken: verifiedNonceToken, password: password })
    return response
  } catch (error) {
    throw Error("participant-login-error-password-reset-failed", { cause: error })
  }
}

export async function getProjectByCode(projectCode: string) {
  try {
    const response = await getRequest(`api/researchkit/administeredstudyenrollment/studies/${projectCode}?offset=${getCurrentOffset()}`)
    return response
  } catch (error) {
    return null
  }
}

export async function getProjectByLinkKey(linkKey: string) {
  try {
    const response = await getRequest(`api/mydatahelps/registrationlink/${linkKey}`)
    return response
  } catch (error) {
    return null
  }
}

export async function trackRegistrationEvent(eventType: string, page: string, emailOrPhone: string, visitId: string, utmParameters: UTMParameters, clickIdParameters: ClickIdParameters, projectCode: string | undefined) {
  try {
    const response = await postRequest(`api/patientopenregistrationV2/trackEvent`, {
      BaseUrl: `${window.location.protocol}//${window.location.host}/`,
      EventType: eventType,
      Page: page,
      EmailOrPhone: emailOrPhone,
      ClientLocalTimestamp: toISOString(new Date()),
      Platform: "Web",
      VisitID: visitId,
      ProjectCode: projectCode,
      ...utmParameters,
      ...clickIdParameters
    })
    return response
  } catch (error) {
    return null
  }
}

export async function validateIdentifier(projectCode: string, identifier: string) {
  try {
    const response = await postRequest(`api/researchkit/administeredstudyenrollment/studies/${projectCode}/validateidentifier`, identifier)
    return response
  } catch (error) {
    throw Error('validate-identifier-failed', { cause: error })
  }
}

function getCurrentOffset() {
  return encodeURIComponent(format(new Date(), "xxxxx"));
}